import { Injectable } from '@angular/core';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/de';
import { CustomDateAdapter } from './custom-date.adapter';

dayjs.extend(customParseFormat);
dayjs.locale('de');

@Injectable()
export class MonthYearDateAdapter extends CustomDateAdapter {
  override parse(value: string | number | Date): Date | null {
    if (value instanceof Date) {
      return value;
    }

    if (!value) {
      return null;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();

      if (!trimmed.includes('.')) {
        if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
          const timestamp = Date.parse(trimmed);
          return isNaN(timestamp) ? null : new Date(timestamp);
        }
        return null;
      }

      const parts = trimmed.split('.');
      if (parts.length !== 2) {
        return null;
      }

      const [month, year] = parts;

      if (month.length !== 2 || year.length !== 4) {
        return null;
      }

      if (!/^\d+$/.test(month) || !/^\d+$/.test(year)) {
        return null;
      }

      const parsed = dayjs(`01.${trimmed}`, 'DD.MM.YYYY', true);

      if (parsed.isValid()) {
        const parsedMonth = parsed.format('MM');
        const parsedYear = parsed.format('YYYY');

        if (parsedMonth === month && parsedYear === year) {
          return parsed.toDate();
        }
      }

      return null;
    }

    if (typeof value === 'number') {
      return isNaN(value) ? null : new Date(value);
    }

    return null;
  }

  /**
   * Format Date object for display in MM.YYYY format
   */
  override format(date: Date, displayFormat: object): string {
    if (!date || !this.isValid(date)) {
      return '';
    }

    const formatOptions = displayFormat as Intl.DateTimeFormatOptions;

    if (
      formatOptions &&
      !('day' in formatOptions) &&
      'month' in formatOptions &&
      'year' in formatOptions
    ) {
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear());
      return `${month}.${year}`;
    }

    return super.format(date, displayFormat);
  }
}
